/**
 * Turning an uploaded DXF file into the string `parseDxf` reads.
 *
 * `parseDxf` takes a string on purpose — the file format says nothing about
 * character encoding, so deciding one is the caller's job, and this is the
 * caller. The decision matters: the factory's exporter writes legacy AC1003
 * files in Windows-1254 (Turkish), so a block named `YÜZ_270` arrives as the
 * bytes `59 DC 5A 5F 32 37 30`. Read as UTF-8 that is mojibake, and the part
 * name the operator sees — and the name stored in `app.saya_part.name` — is
 * garbage. The geometry survives either way (coordinates and group codes are
 * ASCII), which is exactly why this is easy to miss.
 *
 * The rule is: UTF-8 if the bytes really are UTF-8, Windows-1254 otherwise.
 * A strict decode is the test — `fatal: true` throws on any byte sequence that
 * is not valid UTF-8, and a Windows-1254 file with any Turkish letter in it
 * almost always contains one. Pure ASCII decodes identically under both, so a
 * file with no accented characters is unaffected by the choice.
 *
 * The one case this gets wrong is a Windows-1254 file whose accented bytes
 * happen to form valid UTF-8 (`Ã§`, `Ä±`, …). That is a sequence no Turkish
 * word produces, and guessing it is Windows-1254 would break every genuinely
 * UTF-8 file from a modern exporter — the trade is deliberate and this way
 * round.
 */

/** The encoding the factory's DXF exporter writes: Turkish, single byte. */
const LEGACY_DXF_ENCODING = 'windows-1254'

/**
 * Decodes DXF bytes, preferring UTF-8 and falling back to Windows-1254.
 *
 * Pure and DOM-free so the choice is unit tested rather than argued about.
 */
export function decodeDxfBytes(bytes: ArrayBuffer): string {
  try {
    return new TextDecoder('utf-8', { fatal: true }).decode(bytes)
  } catch {
    return new TextDecoder(LEGACY_DXF_ENCODING).decode(bytes)
  }
}

/** Reads an uploaded DXF file with {@link decodeDxfBytes}. */
export async function readDxfFile(file: Blob): Promise<string> {
  return decodeDxfBytes(await file.arrayBuffer())
}
